import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { 
  Activity, 
  FileText, 
  CheckCircle, 
  DollarSign, 
  ExternalLink,
  ArrowRight,
  MapPin,
  Clock,
  Plus,
  FileUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [recentInspections, setRecentInspections] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, inspectionsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/inspections`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      // Get most recent 5 inspections
      setRecentInspections(inspectionsRes.data.slice(0, 5));
      // Get most recent 5 reports
      setRecentReports(reportsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Navigation helpers - single source of truth
  const navigateToInspection = (inspectionId) => {
    if (inspectionId) {
      navigate(`/inspections/${inspectionId}`);
    } else {
      toast.error('Unable to open inspection - ID unavailable');
    }
  };

  const navigateToReport = (reportId) => {
    if (reportId) {
      navigate(`/reports/${reportId}`);
    } else {
      toast.error('Unable to open report - ID unavailable');
    }
  };

  const getInspectionStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-accent text-white';
      case 'in_progress':
        return 'bg-warning text-white';
      case 'completed':
        return 'bg-success text-white';
      case 'draft':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-secondary';
    }
  };

  const getReportStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-secondary text-secondary-foreground';
      case 'in_progress':
        return 'bg-warning text-white';
      case 'completed':
        return 'bg-success text-white';
      case 'approved':
        return 'bg-primary text-white';
      default:
        return 'bg-secondary';
    }
  };

  const statCards = [
    {
      title: 'Active Inspections',
      value: stats?.active_inspections || 0,
      icon: Activity,
      color: 'text-accent',
      bgColor: 'hover:bg-accent/5',
      testId: 'active-inspections-stat',
      onClick: () => navigate('/inspections')
    },
    {
      title: 'Reports In Progress',
      value: stats?.in_progress_reports || 0,
      icon: FileText,
      color: 'text-warning',
      bgColor: 'hover:bg-warning/5',
      testId: 'in-progress-reports-stat',
      onClick: () => navigate('/reports')
    },
    {
      title: 'Completed Reports',
      value: stats?.completed_reports || 0,
      icon: CheckCircle,
      color: 'text-success',
      bgColor: 'hover:bg-success/5',
      testId: 'completed-reports-stat',
      onClick: () => navigate('/reports')
    },
    {
      title: 'Total Valuation',
      value: `£${stats?.total_valuation?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: 'text-primary',
      bgColor: 'hover:bg-primary/5',
      testId: 'total-valuation-stat',
      onClick: () => navigate('/inspections')
    }
  ];

  const handleKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2" data-testid="dashboard-title">
            Welcome back, {user?.email}
          </h1>
          <p className="text-muted-foreground" data-testid="dashboard-role">
            Role: <span className="font-medium capitalize">{user?.role?.replace('_', ' ')}</span>
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statCards.map((stat, index) => (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${stat.bgColor} group`}
                    onClick={stat.onClick}
                    onKeyDown={(e) => handleKeyDown(e, stat.onClick)}
                    tabIndex={0}
                    role="button"
                    aria-label={`${stat.title}: ${stat.value}. Click to view details.`}
                    data-testid={stat.testId}
                  >
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">
                        {stat.title}
                      </CardTitle>
                      <stat.icon className={`h-5 w-5 ${stat.color}`} />
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="text-3xl font-bold text-primary">{stat.value}</div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  className="p-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-accent hover:bg-accent/5 transition-all text-center group"
                  onClick={() => navigate('/inspections')}
                  data-testid="new-inspection-action"
                >
                  <Plus className="h-8 w-8 mx-auto mb-2 text-accent" />
                  <p className="font-medium group-hover:text-accent transition-colors">New Inspection</p>
                </button>
                <button
                  className="p-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-teal-500 hover:bg-teal-50 transition-all text-center group"
                  onClick={() => navigate('/reverse-engineering')}
                  data-testid="import-pdf-action"
                >
                  <FileUp className="h-8 w-8 mx-auto mb-2 text-teal-600" />
                  <p className="font-medium group-hover:text-teal-600 transition-colors">Import from PDF</p>
                </button>
                <button
                  className="p-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-accent hover:bg-accent/5 transition-all text-center group"
                  onClick={() => navigate('/reports')}
                  data-testid="new-report-action"
                >
                  <FileText className="h-8 w-8 mx-auto mb-2 text-accent" />
                  <p className="font-medium group-hover:text-accent transition-colors">New Report</p>
                </button>
                <button
                  className="p-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-accent hover:bg-accent/5 transition-all text-center group"
                  onClick={() => navigate('/inspections')}
                  data-testid="view-all-action"
                >
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-accent" />
                  <p className="font-medium group-hover:text-accent transition-colors">View All</p>
                </button>
              </CardContent>
            </Card>

            {/* Recent Inspections & Reports */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Inspections */}
              <Card data-testid="recent-inspections-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-accent" />
                    Recent Inspections
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/inspections')}
                    data-testid="view-all-inspections-btn"
                  >
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {recentInspections.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No inspections yet</p>
                  ) : (
                    recentInspections.map((inspection, index) => {
                      const hasValidId = inspection.id && inspection.id !== '';
                      
                      return (
                        <motion.div
                          key={inspection.id || index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`p-3 rounded-lg border transition-all ${
                            hasValidId 
                              ? 'cursor-pointer hover:bg-accent/5 hover:border-accent/50 group' 
                              : 'opacity-60 cursor-not-allowed'
                          }`}
                          onClick={() => hasValidId && navigateToInspection(inspection.id)}
                          onKeyDown={(e) => hasValidId && handleKeyDown(e, () => navigateToInspection(inspection.id))}
                          tabIndex={hasValidId ? 0 : -1}
                          role="button"
                          aria-label={hasValidId ? `Open inspection: ${inspection.property_address}` : 'Inspection unavailable'}
                          data-testid={`recent-inspection-${inspection.id || index}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate ${
                                hasValidId ? 'group-hover:text-accent group-hover:underline' : ''
                              }`}>
                                {inspection.property_address || 'Unknown Address'}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  {new Date(inspection.created_at).toLocaleDateString()}
                                </span>
                                {!hasValidId && (
                                  <span className="text-xs text-destructive">• Unavailable</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {hasValidId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigateToInspection(inspection.id);
                                  }}
                                  data-testid={`open-inspection-btn-${inspection.id}`}
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Open
                                </Button>
                              )}
                              <Badge className={getInspectionStatusColor(inspection.status)}>
                                {inspection.status?.replace('_', ' ') || 'Unknown'}
                              </Badge>
                            </div>
                          </div>
                          {inspection.valuation_amount > 0 && (
                            <p className="text-sm text-muted-foreground mt-1">
                              <DollarSign className="h-3 w-3 inline mr-1" />
                              £{inspection.valuation_amount.toLocaleString()}
                            </p>
                          )}
                        </motion.div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* Recent Reports */}
              <Card data-testid="recent-reports-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-warning" />
                    Recent Reports
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/reports')}
                    data-testid="view-all-reports-btn"
                  >
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {recentReports.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No reports yet</p>
                  ) : (
                    recentReports.map((report, index) => {
                      const hasValidId = report.id && report.id !== '';
                      const hasInspectionId = report.inspection_id && report.inspection_id !== '';
                      
                      return (
                        <motion.div
                          key={report.id || index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`p-3 rounded-lg border transition-all ${
                            hasValidId 
                              ? 'cursor-pointer hover:bg-warning/5 hover:border-warning/50 group' 
                              : 'opacity-60 cursor-not-allowed'
                          }`}
                          onClick={() => hasValidId && navigateToReport(report.id)}
                          onKeyDown={(e) => hasValidId && handleKeyDown(e, () => navigateToReport(report.id))}
                          tabIndex={hasValidId ? 0 : -1}
                          role="button"
                          aria-label={hasValidId ? `Open report: ${report.title}` : 'Report unavailable'}
                          data-testid={`recent-report-${report.id || index}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate ${
                                hasValidId ? 'group-hover:text-warning group-hover:underline' : ''
                              }`}>
                                {report.title || 'Untitled Report'}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  {new Date(report.updated_at).toLocaleDateString()}
                                </span>
                                {!hasValidId && (
                                  <span className="text-xs text-destructive">• Unavailable</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {hasInspectionId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigateToInspection(report.inspection_id);
                                  }}
                                  data-testid={`open-linked-inspection-btn-${report.id}`}
                                >
                                  <MapPin className="h-3 w-3 mr-1" />
                                  Inspection
                                </Button>
                              )}
                              {hasValidId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigateToReport(report.id);
                                  }}
                                  data-testid={`open-report-btn-${report.id}`}
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Open
                                </Button>
                              )}
                              <Badge className={getReportStatusColor(report.status)}>
                                {report.status?.replace('_', ' ') || 'Unknown'}
                              </Badge>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
